import { Injectable, NgZone } from '@angular/core';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import {AccountsService} from '../accounts/accounts.service';
import {Observer} from 'rxjs/Observer';
import {NavigationService} from '../navigation/navigation.service';
import {AuthService} from '../auth/auth.service';
import {} from '@types/googlemaps';

@Injectable()
export class MapService {

  private defaultCoords = {lat: 47.5432537, lng: -122.0782054};

  private map: any;
  private infowindow: any;

  coords: any;

  isExpanded: boolean;

  // places

  public placesSearchService:  google.maps.places.PlacesService;
  private searchString: string;
  private lastSearchString: string;
  public savedSearches: any[] = [];
  public redoSearchAvailable: boolean;

  private placeMarkers: any[] = [];
  private selectedPlaceMarker;

  private placeManager: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public selectPlace: Observable<any>;

  private accountManager: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public selectAccount: Observable<any>;

  private lassoManager: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public completeLasso: Observable<any[]>;

  private placeMarkerIcon = {
    url: 'assets/home (1).png',
    scaledSize: new google.maps.Size(24, 24),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(12, 12)
  };

  private placeMarkerIconHighlight = {
    url: 'assets/home.png',
    scaledSize: new google.maps.Size(32, 32),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(16, 16)
  };

  // accounts

  private accountMarkers: any[] = [];
  private selectedAccountMarker;

  private defaultAccountFillColor = '#999';
  private defaultAccountStrokeColor = '#000';

  private selectedAccountFillColor = '#fff';
  private selectedAccountStrokeColor = '#f00';

  private lassoAccountFillColor = '#FFCCCC';
  private lassoAccountStrokeColor = '#f00';

  private colors: any[];

  public userColors: any[];

  // layers

  private trafficLayer;
  private threeZipLayer;
  private fiveZipLayer;

  private threeZipTable = '1mOP3DwNAf6Nj_0eKbbVxlRP3FSXLYwVUAGL7g8oH';
  private fiveZipTable = '1fsM9Yu4nRZm3RpsVSZbD5JVKtXSMZqnPKmptrkt5';

  public showTrafficLayer;
  public showSatelliteLayer;
  public show3Zip;
  public show5Zip;

  private threeZipListener;
  private fiveZipListener;

  // directions

  private directionsService: any;

  private directionsDisplay;

  // lasso

  private lassoSelectedAccountMarkers: any[] = [];
  private lasso: any;
  public lassoMode: boolean;

  // visualize

  excludedAccounts = [];

  excludedUsers = [];

  constructor(private zone: NgZone,
              private authService: AuthService,
              private accountsService: AccountsService,
              private navigationService: NavigationService) {
    this.selectPlace = this.placeManager.asObservable();
    this.selectAccount = this.accountManager.asObservable();
    this.completeLasso = this.lassoManager.asObservable();

    this.accountsService.selectAccount.subscribe(account => {
      if (account) {
        const accountMarker = this.accountMarkers.find(am => am.account === account);
        if (accountMarker) {
          this.selectAccountMarker(accountMarker);
        }
      }
    });

    this.accountsService.deleteAccount.subscribe(id => {
      if (id) {
        this.deleteAccountMarker(id);
      }
    });

    this.authService.logOut.subscribe((doLogout) => {
      if (doLogout) {
        this.placeManager.next(null);
        this.accountManager.next(null);

        this.showTrafficLayer = false;
        this.showSatelliteLayer = false;
        this.show3Zip = false;
        this.show5Zip = false;

        this.selectedPlaceMarker = null;
        this.selectedAccountMarker = null;

        this.removeMarkers(this.placeMarkers);
        this.removeMarkers(this.accountMarkers);

        this.map = null;
      }
    });
  }

  public zoomIn() {
    this.map.setZoom(this.map.getZoom() + 1);
  }

  public zoomOut() {
    this.map.setZoom(this.map.getZoom() - 1);
  }

  public initMap(id) {
    const mapProp = {
      center: new google.maps.LatLng(this.defaultCoords.lat, this.defaultCoords.lng),
      zoom: 14,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: false,
      zoomControl: false,
      streetViewControl: false,
      draggable: true,
      styles: [
        {
          'featureType': 'administrative',
          'elementType': 'labels.text.fill',
          'stylers': [
            {
              'color': '#444444'
            }
          ]
        },
        {
          'featureType': 'landscape',
          'elementType': 'all',
          'stylers': [
            {
              'color': '#f2f2f2'
            }
          ]
        },
        {
          'featureType': 'poi',
          'elementType': 'all',
          'stylers': [
            {
              'visibility': 'off'
            }
          ]
        },
        {
          'featureType': 'road',
          'elementType': 'all',
          'stylers': [
            {
              'saturation': -100
            },
            {
              'lightness': 45
            }
          ]
        },
        {
          'featureType': 'road.highway',
          'elementType': 'all',
          'stylers': [
            {
              'visibility': 'simplified'
            }
          ]
        },
        {
          'featureType': 'road.arterial',
          'elementType': 'labels.icon',
          'stylers': [
            {
              'visibility': 'off'
            }
          ]
        },
        {
          'featureType': 'transit',
          'elementType': 'all',
          'stylers': [
            {
              'visibility': 'off'
            }
          ]
        },
        {
          'featureType': 'water',
          'elementType': 'all',
          'stylers': [
            {
              'color': '#46bcec'
            },
            {
              'visibility': 'on'
            }
          ]
        }
      ]
    };

    this.map = new google.maps.Map(document.getElementById(id), (<any>mapProp));

    (<any>this.map.getDiv()).addEventListener('touchmove', function(e) { e.preventDefault(); }, { passive: false });

    this.infowindow = new google.maps.InfoWindow();

    this.placesSearchService = new google.maps.places.PlacesService(this.map);

    this.directionsService = new google.maps.DirectionsService();

    const rendererOptions = {
      preserveViewport: true,
      suppressInfoWindows: true,
      markerOptions: {
        clickable: false
      }
    };

    this.directionsDisplay = new google.maps.DirectionsRenderer(<any>rendererOptions);

    this.directionsDisplay.setMap(this.map);

    google.maps.event.addListener(this.map, 'dragend', () => {
      this.zone.run(() => {
        if (this.searchString || this.savedSearches.find(s => s.active)) {
          this.redoSearchAvailable = true;
        }
      });
    });

    google.maps.event.addListener(this.map, 'zoom_changed', () => {
      this.zone.run(() => {
        if (this.searchString || this.savedSearches.find(s => s.active)) {
          this.redoSearchAvailable = true;
        }
      });
    });
  }

  clearFilters() {
    this.colors = [];
    this.excludedAccounts = [];
  }

  public placeAccountMarkers(accounts: any[]) {
    this.removeMarkers(this.accountMarkers);
    accounts.forEach(account => {
      this.addAccountMarker(account);
    });
  }

  public addAccountMarker(account) {
    const position = new google.maps.LatLng(account.coords[1], account.coords[0]);

    const marker = new google.maps.Marker(<any>{
      position: position,
      account: account,
      map: this.map,
      icon: this.getDefaultAccountIcon(account)
    });

    google.maps.event.addListener(marker, 'click', () => {
      this.zone.run(() => {
        this.selectAccountMarker(marker);
        this.accountManager.next(marker['account']);
      });
    });

    this.accountMarkers.push(marker);
  }

  private selectAccountMarker(marker) {
    if (this.selectedPlaceMarker) {
      this.selectedPlaceMarker.setIcon(this.placeMarkerIcon);
      this.selectedPlaceMarker = null;
    }

    if (this.selectedAccountMarker) {
      this.selectedAccountMarker.setIcon(this.getDefaultAccountIcon(this.selectedAccountMarker.account));
    }
    this.selectedAccountMarker = marker;
    this.selectedAccountMarker.setIcon(this.getSelectedAccountIcon());
    this.accountsService.selectedAccount = marker.account;
  }

  public deleteAccountMarker(id) {
    const marker = this.accountMarkers.find(am => am.account._id === id);
    this.removeMarkers([marker]);
    this.accountMarkers = this.accountMarkers.filter(m => m.account._id !== id);
  }

  private getDefaultAccountIcon(account) {
    if (this.lassoSelectedAccountMarkers.find(m => m.account === account)) {
      return this.getLassoAccountIcon();
    } else {
      const fillColor = this.getFillColor(account);
      return this.getCustomIcon(fillColor, this.defaultAccountStrokeColor, 2);
    }

  }

  private getSelectedAccountIcon() {
    return this.getCustomIcon(this.selectedAccountFillColor, this.selectedAccountStrokeColor, 2);
  }

  private getLassoAccountIcon() {
    return this.getCustomIcon(this.lassoAccountFillColor, this.lassoAccountStrokeColor, 2);
  }

  private getCustomIcon(fillColor, strokeColor, strokeWeight) {
    return {
      path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z M -2,-30 a 2,2 0 1,1 4,0 2,2 0 1,1 -4,0',
      fillColor,
      fillOpacity: 1,
      strokeColor,
      strokeWeight,
      scale: 1,
    };
  }

  toggleTraffic(show?: boolean) {
    if (show) {
      this.showTrafficLayer = show;
    } else {
      this.showTrafficLayer = !this.showTrafficLayer;
    }

    if (this.showTrafficLayer) {
      this.trafficLayer = new google.maps.TrafficLayer();
      this.trafficLayer.setMap(this.map);
    } else {
      this.trafficLayer.setMap(null);
      this.trafficLayer = null;
    }
  }

  toggleSatellite(show?: boolean) {
    if (show) {
      this.showSatelliteLayer = show;
    } else {
      this.showSatelliteLayer = !this.showSatelliteLayer;
    }

    if (this.showSatelliteLayer) {
      this.map.setMapTypeId(google.maps.MapTypeId.HYBRID);
    } else {
      this.map.setMapTypeId(google.maps.MapTypeId.ROADMAP);
    }
  }

  toggle3Zip(show?: boolean) {
    if (typeof show !== 'undefined') {
      this.show3Zip = show;
    } else {
      this.show3Zip = !this.show3Zip;
    }

    if (this.show3Zip) {
      this.toggle5Zip(false);
      this.threeZipLayer = new google.maps.FusionTablesLayer({
        query: {
          select: 'geometry',
          from: this.threeZipTable
        },
        styles: [{
          polygonOptions: {
            fillColor: '#fff',
            fillOpacity: 0.01,
            strokeColor: '#0000ff'
          },
          markerOptions: {
            iconName: 'small_blue'
          }
        }],
        suppressInfoWindows: true
      });
      this.threeZipLayer.setMap(this.map);
      this.threeZipListener = google.maps.event.addListener(this.threeZipLayer, 'click', (e) => {
        if (this.lassoMode) { return; }
        this.windowControl(e, `3-digit ZIP: ${e.row['ZIP3'].value}`);
      });
    } else {
      if (this.threeZipLayer) {
        this.threeZipLayer.setMap(null);
        this.threeZipLayer = null;
        google.maps.event.removeListener(this.threeZipListener);
      }
    }
  }

  toggle5Zip(show?: boolean) {
    if (typeof show !== 'undefined') {
      this.show5Zip = show;
    } else {
      this.show5Zip = !this.show5Zip;
    }

    if (this.show5Zip) {
      this.toggle3Zip(false);
      this.fiveZipLayer = new google.maps.FusionTablesLayer({
        query: {
          select: 'geometry',
          from: this.fiveZipTable
        },
        styles: [{
          polygonOptions: {
            fillColor: '#fff',
            fillOpacity: 0.01,
            strokeColor: '#0000ff'
          },
          markerOptions: {
            iconName: 'small_blue'
          }
        }],
        suppressInfoWindows: true
      });
      this.fiveZipLayer.setMap(this.map);
      this.fiveZipListener = google.maps.event.addListener(this.fiveZipLayer, 'click', (e) => {
        if (this.lassoMode) { return; }
        this.windowControl(e, `ZIP: ${e.row['ZIP_CODE'].value}`);
      });
    } else {
      if (this.fiveZipLayer) {
        this.fiveZipLayer.setMap(null);
        this.fiveZipLayer = null;
        google.maps.event.removeListener(this.fiveZipListener);
      }
    }
  }

  private windowControl(e, content) {
    this.infowindow.setOptions({
      content: content,
      position: e.latLng,
      pixelOffset: e.pixelOffset
    });
    this.infowindow.open(this.map);
  }

  public setSearches(searches) {
    this.savedSearches = searches;
    this.searchPlaces();
  }

  public setSearchString(str: string) {
    this.searchString = str;
  }

  public searchPlaces() {
    if (!this.searchString && !this.savedSearches.find(s => s.active)) {
      this.removeMarkers(this.placeMarkers);
      return;
    }

    if (this.searchString !== this.lastSearchString) {
      this.removeMarkers(this.placeMarkers);
    }
    this.lastSearchString = this.searchString;

    let searches = this.savedSearches.map(s => s.value);
    if (this.searchString) {
      searches.unshift(this.searchString);
    }

    let searchStr;
    if (searches.length === 1) {
      searchStr = searches[0];
    } else {
      searches = searches.map(s => `(${s})`);
      searchStr = searches.join(' OR ');
    }

    this.placesSearchService.nearbySearch({bounds: this.map.getBounds(), keyword: searchStr}, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        this.zone.run(() => {
          this.redoSearchAvailable = false;
        });
        results.forEach(result => {
          if (result.geometry.location) {
            if (!this.map) {
              return;
            }

            const position = new google.maps.LatLng(result.geometry.location.lat(), result.geometry.location.lng());

            const marker = new google.maps.Marker(<any>{
              position: position,
              place_object: result,
              icon: this.placeMarkerIcon,
              map: this.map
            });

            google.maps.event.addListener(marker, 'click', () => {
              this.zone.run(() => {
                if (this.selectedAccountMarker) {
                  this.selectedAccountMarker.setIcon(this.getDefaultAccountIcon(this.selectedAccountMarker.account));
                  this.selectedAccountMarker = null;
                }
                if (this.selectedPlaceMarker) {
                  this.selectedPlaceMarker.setIcon(this.placeMarkerIcon);
                }
                this.selectedPlaceMarker = marker;
                this.selectedPlaceMarker.setIcon(this.placeMarkerIconHighlight);
                this.placeManager.next(marker['place_object']);
              });
            });

            this.placeMarkers.push(marker);
          }
        });
      }
    });
  }

  private removeMarkers(markers: any[]) {
    markers.forEach(marker => {
      if (marker) {
        marker.setMap(null);
        marker = null;
      }
    });
    markers.length = 0;
  }

  public center(lat, lng) {
    if (lat && lng) {
      this.map.setCenter({ lat, lng });
      this.map.setZoom(16);
    }
  }

  public toggleLasso() {
    this.lassoMode = !this.lassoMode;

    if (this.lassoMode) {
      this.clearLassoSelection();

      this.disableMap();

      this.map.addListener('mousedown', () => {
        this.draw();
      });
    } else {
      google.maps.event.clearListeners(this.map, 'mousedown');
      this.enableMap();
    }
  }

  public deleteFromLasso(accountId) {
    const markerToDelete = this.lassoSelectedAccountMarkers.find(marker => marker.account._id !== accountId);

    this.lassoSelectedAccountMarkers = this.lassoSelectedAccountMarkers.filter(m => m !== markerToDelete);

    if (!this.selectedAccountMarker || this.selectedAccountMarker !== markerToDelete) {
      markerToDelete.setIcon(this.getDefaultAccountIcon(markerToDelete.account));
    }
  }

  public clearLassoSelection() {
    if (this.lasso) {
      this.lasso.setMap(null);
      this.lasso = null;
    }
    if (this.lassoSelectedAccountMarkers) {
      this.lassoSelectedAccountMarkers.forEach(marker => {
        if (this.selectedAccountMarker && this.selectedAccountMarker === marker) {
          return;
        }
        marker.setIcon(this.getDefaultAccountIcon(marker.account));
      });
      this.lassoSelectedAccountMarkers = [];
    }
  }

  private draw() {
    this.lasso = new google.maps.Polyline({map: this.map, clickable: false});

    const move = this.map.addListener('mousemove', (e: any) => {
      if (event['placeId']) {
        e.stop();
      }
      this.lasso.getPath().push(e.latLng);
      e.stop();
    });

    this.map.addListener('mouseup', (e) => {
      google.maps.event.removeListener(move);
      const path = this.lasso.getPath();
      this.lasso.setMap(null);
      this.lasso = new google.maps.Polygon(<any>{map: this.map, path: path});

      this.accountMarkers.forEach(marker => {
        if (google.maps.geometry.poly.containsLocation(marker.getPosition(), this.lasso)) {
          this.lassoSelectedAccountMarkers.push(marker);
          marker.setIcon(this.getLassoAccountIcon());
        }
      });

      google.maps.event.clearListeners(this.map, 'mousedown');
      google.maps.event.clearListeners(this.map, 'mouseup');

      this.enableMap();

      this.lassoMode = false;

      this.lassoManager.next(this.lassoSelectedAccountMarkers.map(marker => marker.account));
      this.navigationService.navigateToLasso();
    });
  }

  private disableMap() {
    this.map.setOptions({
      draggable: false,
      clickable: false,
      zoomControl: false,
      scrollwheel: false,
      disableDoubleClickZoom: false,
    });
  }

  private enableMap() {
    this.map.setOptions({
      draggable: true,
      clickable: true,
      zoomControl: false,
      scrollwheel: true,
      disableDoubleClickZoom: true,
    });
  }

  public getRoute(request) {
    return new Observable((observer: Observer<any>) => {
      this.directionsService.route(request, (result, status) => {
        this.zone.run(() => {
          if (status === 'OK') {
            this.directionsDisplay.setDirections(result);
            observer.next(result);
            observer.complete();
          } else {
            observer.error(status);
          }
        });
      });
    });
  }

  public clearRoute() {
    this.directionsDisplay.setDirections({routes: []});
  }

  toggleExpanded(isExpanded: boolean) {
    this.isExpanded = isExpanded;
  }

  getFillColor(account) {
    let color, userColor;

    if (this.userColors) {
      const foundColorValue = this.userColors.find(c => c.value === account.createdBy);
      userColor = foundColorValue && foundColorValue.color;
    }

    if (!this.colors || !this.colors.length) {
      color = userColor || this.defaultAccountFillColor;
    } else {
        const foundColorValue = this.colors.find(c => {
          return c.accounts.indexOf(account._id) > -1;
        });
        color = foundColorValue ? foundColorValue.color : userColor || this.defaultAccountFillColor;
    }
    return color;
  }

  setColors(colors) {
    this.colors = colors;
    this.accountMarkers.forEach(marker => {
      if (marker !== this.selectedAccountMarker) {
        marker.setIcon(this.getDefaultAccountIcon(marker.account));
      }
    });
  }

  filterAccounts(excludedAccounts: any[]) {
    this.excludedAccounts = excludedAccounts;
    this.accountMarkers.forEach(marker => {
      if (this.excludedAccounts.indexOf(marker.account._id) > -1) {
        marker.setMap(null);
      } else if (!marker.getMap()) {
        marker.setMap(this.map);
      }
    });
  }

  filterAccountsByUsers(excludedUsers: any[]) {
    this.excludedUsers = excludedUsers;
    this.accountMarkers.forEach(marker => {
      if (this.excludedUsers.indexOf(marker.account.createdBy) > -1) {
        marker.setMap(null);
      } else if (!marker.getMap()) {
        marker.setMap(this.map);
      }
    });
  }

}



// WEBPACK FOOTER //
// ./client/app/core/map/map.service.ts